import * as React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaInfoCircle } from "@react-icons/all-files/fa/FaInfoCircle";
import styled from "@emotion/styled";
import { contentTakedown } from "@teachers/easel-global-utils";
import Text from "../global/Text";
import {
  designTokens,
  easelColors,
  easelFontFamily,
} from "../../helpers/constants";
import "./ContentViolationLabel.scss";

const { ContentViolationFlagType } = contentTakedown;

export interface ContentViolationLabelProps {
  isSellerContent: boolean;
  flagType: contentTakedown.ContentViolationFlagType;
  contactUsHref: string;
  copyrightPolicyHref: string;
  inappropriatePolicyHref: string;
}

const LABEL_MESSAGE_SELLER = "Content issue";
const LABEL_MESSAGE__NON_SELLER = "Removed";

const icTooltipMessage = (
  contactUsHref: string,
  inappropriatePolicyHref: string
) => (
  <span>
    This resource has been deactivated in accordance with our{" "}
    <a href={inappropriatePolicyHref}>Inappropriate Content Policy</a>. If you
    have any questions, please <a href={contactUsHref}>Contact Us</a>.
  </span>
);

const ipTooltipMessage = (copyrightPolicyHref: string) => (
  <span>
    This product has been deactivated in accordance with our{" "}
    <a href={copyrightPolicyHref}>Copyright Policy</a>. You have the right to
    file a counter-notification. Please review the{" "}
    <a href={copyrightPolicyHref}>Copyright Policy</a> for instructions.
  </span>
);

const unknownTooltipMessage = (contactUsHref: string) => (
  <span>
    This resource has been removed from the site. We apologize for any
    inconvenience. If you have any questions, please{" "}
    <a href={contactUsHref}>Contact Us</a>
  </span>
);

const nonSellerTooltipMessage = (contactUsHref: string) => (
  <span>
    This resource has been removed from the site. You can still review student
    work but students can no longer access the content. We apologize for any
    inconvenience. If you have any questions, please{" "}
    <a href={contactUsHref}>Contact Us</a>.
  </span>
);

export const ContentViolationLabel: React.FC<ContentViolationLabelProps> = ({
  isSellerContent,
  flagType,
  contactUsHref,
  copyrightPolicyHref,
  inappropriatePolicyHref,
}) => {
  const label = isSellerContent
    ? LABEL_MESSAGE_SELLER
    : LABEL_MESSAGE__NON_SELLER;

  const [show, setShow] = React.useState(false);

  let tooltip;

  if (!isSellerContent) {
    tooltip = nonSellerTooltipMessage(contactUsHref);
  } else if (flagType === ContentViolationFlagType.IC) {
    tooltip = icTooltipMessage(contactUsHref, inappropriatePolicyHref);
  } else if (flagType === ContentViolationFlagType.IP) {
    tooltip = ipTooltipMessage(copyrightPolicyHref);
  } else {
    tooltip = unknownTooltipMessage(contactUsHref);
  }

  return (
    <Wrapper>
      <WrapperLabel isSellerContent={isSellerContent}>
        <Text>{label}</Text>
      </WrapperLabel>
      <OverlayTrigger
        placement="bottom"
        show={show}
        overlay={
          <Tooltip
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
            id="tooltip"
            data-testid="ContentViolationLabel__tooltip_text"
            className="ContentViolationLabel__tooltip"
          >
            {tooltip}
          </Tooltip>
        }
      >
        <WrapperIcon>
          <FaInfoCircle
            data-testid="ContentViolationLabel__tooltip_icon"
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
          />
        </WrapperIcon>
      </OverlayTrigger>
    </Wrapper>
  );
};

const Wrapper = styled.div({
  fontFamily: easelFontFamily,
  display: "inline-flex",
  flexDirection: "row",
  alignItems: "center",
});

const WrapperIcon = styled.div({
  cursor: "pointer",
});

const WrapperLabel = styled.div(
  ({ isSellerContent }: { isSellerContent: boolean }) => ({
    borderRadius: designTokens["ds-border-radius-sm"],
    background: isSellerContent
      ? easelColors.easelCoral100
      : easelColors.easelGold100,
    color: isSellerContent
      ? easelColors.easelCoral600
      : easelColors.easelCharcoal,
    paddingTop: designTokens["ds-spacing-xxs"],
    paddingBottom: designTokens["ds-spacing-xxs"],
    paddingLeft: designTokens["ds-space-2x"],
    paddingRight: designTokens["ds-space-2x"],
    marginRight: designTokens["ds-spacing-sm"],
  })
);
